// @flow
// $FlowIssue
import { useState } from 'react';
import { deleteByToken } from './cloudinaryApi';
import {
  FileSystemResource,
  CloudResource,
  type UploadResponse,
  RemoteResource,
  ResourceTypes,
} from './ResourceTypes';
import { Map as ImmutableMap } from 'immutable';

export const DEFAULT_MAX_MEDIA_ITEMS = 4;

export type MediaRecord = {
  id: string,
  file?: File,
  caption: string,
  resource_type: string,
  delete_token?: string,
  url?: string,
};

export function serializeResponseMedia(
  media: Map<string, CloudResource>,
  captions: Map<string, string>
) {
  return Array.from(media.values())
    .filter(ResourceTypes.isSerializable)
    .map(item => item.serialize(captions));
}

function deleteUpload(delete_token?: string) {
  if (typeof delete_token === 'string') {
    deleteByToken(delete_token);
  }
}

export function useMediaUpload(maxItems: number) {
  const [media, setMedia] = useState(ImmutableMap());
  const [captions, setCaptions] = useState(ImmutableMap());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const uploadsHaveResolved = Array.from(media.values()).every(
    ResourceTypes.isSerializable
  );
  const canPersistMedia =
    uploadsHaveResolved === true && media.size <= maxItems;

  function addFiles(files: Array<FileSystemResource | RemoteResource>) {
    setMedia(prevMedia => {
      return files.reduce((media, item) => {
        return media.has(item.id) ? media : media.set(item.id, item);
      }, prevMedia);
    });
  }

  function removeFile(id: string) {
    const { delete_token } = media.get(id) || {};
    deleteUpload(delete_token);

    setMedia(prevMedia => {
      return prevMedia.delete(id);
    });

    setCaptions(prevCaptions => {
      return prevCaptions.delete(id);
    });
  }

  function uploadFile(id: string, response: UploadResponse | Error) {
    if (response instanceof Error) {
      // remove failed upload
      const timeoutId = setTimeout(() => {
        removeFile(id);
        clearTimeout(timeoutId);
      }, 2000);
      return;
    }

    setMedia(prevMedia => {
      // $$FlowIssue
      return prevMedia.set(id, new CloudResource(id, response));
    });
  }

  function setCaption(id: string, caption: string) {
    setCaptions(prevCaptions => {
      return prevCaptions.set(id, caption);
    });
  }

  function clearFiles() {
    // clear uploads which will not be persisted
    Array.from(media.values())
      .map(rec => rec.delete_token)
      .forEach(deleteUpload);

    reset();
  }

  function toggleModal() {
    setIsModalOpen(current => !current);
  }

  function reset() {
    setMedia(prevMedia => prevMedia.clear());
    setCaptions(prevCaptions => prevCaptions.clear());
    setIsModalOpen(false);
  }

  return {
    media,
    captions,
    canPersistMedia,
    addFiles,
    removeFile,
    uploadFile,
    setCaption,
    clearFiles,
    reset,
    isModalOpen,
    toggleModal,
    maxItems,
  };
}



// WEBPACK FOOTER //
// ./src/components/ContentSection/__future__/MediaUpload/useMediaUpload.js